import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Megaphone, Plus, Search } from 'lucide-react'
import { api } from '@/lib/api'

type CampaignKind = 'EMAIL' | 'SMS' | 'WHATSAPP_TEMPLATE' | 'WHATSAPP_BROADCAST'
type CampaignStatus =
  | 'DRAFT'
  | 'SCHEDULED'
  | 'SENDING'
  | 'SENT'
  | 'PAUSED'
  | 'FAILED'
  | 'CANCELLED'

interface Campaign {
  id: string
  kind: CampaignKind
  status: CampaignStatus
  name: string
  smartListId: string
  smartList: { id: string; name: string } | null
  totalRecipients: number
  sent: number
  skipped: number
  failed: number
  scheduledAt: string | null
  dispatchedAt: string | null
  createdAt: string
  updatedAt: string
}

const STATUS_LABEL: Record<CampaignStatus, string> = {
  DRAFT: 'Borrador',
  SCHEDULED: 'Programada',
  SENDING: 'Enviando',
  SENT: 'Enviada',
  PAUSED: 'Pausada',
  FAILED: 'Fallida',
  CANCELLED: 'Cancelada',
}

const KIND_LABEL: Record<CampaignKind, string> = {
  EMAIL: 'Email',
  SMS: 'SMS',
  WHATSAPP_TEMPLATE: 'WhatsApp Plantilla',
  WHATSAPP_BROADCAST: 'WhatsApp Broadcast',
}

function statusPillClass(s: CampaignStatus): string {
  switch (s) {
    case 'DRAFT':
      return 'bg-ink-900/10 text-ink-700/70'
    case 'SCHEDULED':
      return 'bg-blueprint-50 text-blueprint-700'
    case 'SENDING':
      return 'bg-amber-50 text-amber-700'
    case 'SENT':
      return 'bg-emerald-50 text-emerald-700'
    case 'PAUSED':
      return 'bg-amber-100 text-amber-900'
    case 'FAILED':
      return 'bg-red-50 text-red-700'
    case 'CANCELLED':
      return 'bg-ink-900/10 text-ink-700/60'
  }
}

export function CampaignsPage() {
  const [items, setItems] = useState<Campaign[]>([])
  const [filterStatus, setFilterStatus] = useState<CampaignStatus | ''>('')
  const [filterKind, setFilterKind] = useState<CampaignKind | ''>('')
  const [query, setQuery] = useState('')
  const [err, setErr] = useState<string | null>(null)

  async function load() {
    setErr(null)
    try {
      const params = new URLSearchParams()
      if (filterStatus) params.set('status', filterStatus)
      if (filterKind) params.set('kind', filterKind)
      if (query.trim()) params.set('q', query.trim())
      const qs = params.toString()
      const r = await api<{ items: Campaign[] }>(
        `/v1/marketing/campaigns${qs ? `?${qs}` : ''}`,
      )
      setItems(r.items)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterKind])

  // Debounce sobre el query: refresca la lista 300ms tras dejar de teclear.
  // Evita que el operador tenga que recordar pulsar Enter o el botón Filtrar
  // tras vaciar el campo.
  useEffect(() => {
    const t = setTimeout(() => {
      void load()
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  return (
    <div className="p-6 max-w-6xl">
      <header className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-medium tracking-tight flex items-center gap-2">
            <Megaphone className="size-5 text-blueprint-700" />
            Campañas
            <span className="bp-pill ml-2 text-[10px]">{items.length}</span>
          </h1>
          <p className="text-xs font-mono text-ink-700/60">
            envíos masivos · EMAIL / SMS / WhatsApp · snapshot de recipients (anti-drift) ·
            guards de consent + DND + supresión obligatorios
          </p>
        </div>
        <Link to="/marketing/campaigns/new" className="bp-btn-primary">
          <Plus className="size-3" /> Nueva campaña
        </Link>
      </header>

      <div className="bp-card p-3 mb-4 grid grid-cols-12 gap-2">
        <div className="col-span-5 relative">
          <Search className="size-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-ink-700/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void load()}
            placeholder="buscar por nombre o asunto…"
            className="w-full pl-7 border border-ink-900/15 rounded px-2 py-1.5 text-sm"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as CampaignStatus | '')}
          className="col-span-3 border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white"
        >
          <option value="">Todos los estados</option>
          {(Object.keys(STATUS_LABEL) as CampaignStatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <select
          value={filterKind}
          onChange={(e) => setFilterKind(e.target.value as CampaignKind | '')}
          className="col-span-3 border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white"
        >
          <option value="">Todos los canales</option>
          {(Object.keys(KIND_LABEL) as CampaignKind[]).map((k) => (
            <option key={k} value={k}>
              {KIND_LABEL[k]}
            </option>
          ))}
        </select>
        <button type="button" onClick={() => void load()} className="bp-btn col-span-1">
          Filtrar
        </button>
      </div>

      {err && (
        <div className="mb-3 text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          {err}
        </div>
      )}

      <div className="bp-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-alt text-xs font-mono uppercase tracking-wider text-ink-700/60">
            <tr>
              <th className="px-3 py-2 text-left">Nombre</th>
              <th className="px-3 py-2 text-left">Canal</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-left">SmartList</th>
              <th className="px-3 py-2 text-right">Recipients</th>
              <th className="px-3 py-2 text-right">Enviados</th>
              <th className="px-3 py-2 text-right">Actualizada</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-ink-900/10 hover:bg-surface-alt">
                <td className="px-3 py-2">
                  <Link
                    to={`/marketing/campaigns/${c.id}`}
                    className="font-medium text-blueprint-700"
                  >
                    {c.name}
                  </Link>
                </td>
                <td className="px-3 py-2">
                  <span className="bp-pill">{KIND_LABEL[c.kind]}</span>
                </td>
                <td className="px-3 py-2">
                  <span className={['bp-pill', statusPillClass(c.status)].join(' ')}>
                    {c.status === 'SENDING' && (
                      <span className="inline-block size-1.5 rounded-full bg-amber-500 animate-pulse mr-1 align-middle" />
                    )}
                    {STATUS_LABEL[c.status]}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">
                  {c.smartList ? (
                    <Link
                      to={`/marketing/smart-lists`}
                      className="text-blueprint-700"
                    >
                      {c.smartList.name}
                    </Link>
                  ) : (
                    <span className="text-ink-700/40">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono">{c.totalRecipients}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {c.sent}
                  {c.totalRecipients > 0 && (
                    <span className="text-[10px] text-ink-700/50 ml-1">
                      /{c.totalRecipients}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs text-ink-700/60">
                  {new Date(c.updatedAt).toLocaleString('es-ES')}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-xs text-ink-700/50">
                  Sin campañas todavía. Crea la primera arriba a la derecha.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
